import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
} from '@angular/fire/firestore';
import {
  watchCollection,
  watchCollectionSafe,
  watchDocSafe,
  getDocResilient,
} from '../../shared/utils/firestore-utils';
import { Auth } from '@angular/fire/auth';
import { Observable, of, combineLatest, switchMap } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AppEvent, UserEventMembership } from '../models/event';
import { VisibilityGroup, EventMember } from '../models/visibility-group';
import { Callable, FN_REGION } from './callable';

/**
 * Plafond de listeners temps réel ouverts pour les événements rejoints.
 *
 * Trente couvre très largement ce qu'un accueil affiche ; au-delà, le coût
 * Firestore et la batterie suivent le nombre d'événements rejoints au fil des
 * ans, pas ce que l'écran montre.
 */
const MAX_JOINED_EVENT_LISTENERS = 30;

/** Date d'adhésion en millisecondes, quelle que soit sa forme (Timestamp, Date). */
function toMillis(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  const ts = value as { toMillis?: () => number; seconds?: number };
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  return typeof ts.seconds === 'number' ? ts.seconds * 1000 : 0;
}

@Injectable({ providedIn: 'root' })
export class Event {
  private firestore = inject(Firestore);
  private callable = inject(Callable);
  private firebaseAuth = inject(Auth);

  // ─── Real-time streams ─────────────────────────────────────────────────────

  watchEvent(eventId: string): Observable<AppEvent | undefined> {
    const ref = doc(this.firestore, `events/${eventId}`);
    return new Observable<AppEvent | undefined>((subscriber) => {
      const unsub = onSnapshot(
        ref,
        (snap) => {
          if (snap.exists()) {
            subscriber.next({ id: snap.id, ...snap.data() } as AppEvent);
          } else {
            subscriber.next(undefined);
          }
        },
        (err) => subscriber.error(err)
      );
      return unsub;
    });
  }

  /**
   * Pointers d'appartenance de l'utilisateur courant (`users/{uid}/eventMemberships`).
   * Contient les événements gérés ET rejoints avec rôle, groupe et date d'adhésion.
   */
  watchMyMemberships(): Observable<UserEventMembership[]> {
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) return of([]);
    const q = query(collection(this.firestore, `users/${uid}/eventMemberships`));
    return watchCollectionSafe<UserEventMembership>(q, 'eventId');
  }

  /**
   * Appartenance de l'utilisateur à un événement, telle qu'affichée.
   *
   * Le pointeur `eventMemberships` fait foi ; à défaut — événement créé avant
   * son introduction — elle se déduit du document de l'événement.
   */
  private withMembership(
    event: AppEvent,
    memberships: Map<string, UserEventMembership>,
    uid: string
  ): AppEvent {
    const mem = memberships.get(event.id);
    const isMgr = event.managerId === uid || mem?.role === 'manager' || mem?.isManager === true;
    const myMembership: UserEventMembership = mem ?? {
      eventId: event.id,
      role: isMgr ? 'manager' : 'contributor',
      isManager: isMgr,
      visibilityGroupName: isMgr ? 'Famille' : 'Membre',
      joinedAt: event.createdAt,
    };
    return { ...event, myMembership };
  }

  /**
   * Événements de l'utilisateur (gérés et rejoints), enrichis de leur `myMembership`.
   *
   * Chaque événement REJOINT est suivi par son propre listener temps réel. Ils
   * sont bornés aux plus récemment rejoints : sans plafond, quelqu'un qui a
   * rejoint quarante événements au fil des ans ouvrait quarante connexions
   * Firestore simultanées à chaque affichage de l'accueil, pour une liste qui
   * n'en montre qu'une poignée. Un listener par document plutôt qu'une requête
   * `in` groupée : un pointeur devenu illisible (événement supprimé, droit
   * retiré) n'y fait échouer que sa propre ligne, là où une requête groupée
   * ferait tomber l'accueil entier.
   */
  watchMyEvents(): Observable<AppEvent[]> {
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) return of([]);

    const managedEventsQuery = query(
      collection(this.firestore, 'events'),
      where('managerId', '==', uid),
      orderBy('createdAt', 'desc')
    );
    const managedEvents$ = watchCollectionSafe<AppEvent>(managedEventsQuery);
    const memberships$ = this.watchMyMemberships();

    return combineLatest([managedEvents$, memberships$]).pipe(
      switchMap(([managedEvents, memberships]) => {
        const membershipMap = new Map<string, UserEventMembership>();
        for (const m of memberships) {
          if (m.eventId) membershipMap.set(m.eventId, m);
        }

        const managedIds = new Set(managedEvents.map((e) => e.id));
        const joinedEventIds = [...memberships]
          .sort((a, b) => toMillis(b.joinedAt) - toMillis(a.joinedAt))
          .map((m) => m.eventId)
          .filter((id): id is string => !!id && !managedIds.has(id))
          .slice(0, MAX_JOINED_EVENT_LISTENERS);

        const decorate = (events: (AppEvent | undefined)[]): AppEvent[] =>
          events
            .filter((ev): ev is AppEvent => !!ev)
            .map((ev) => this.withMembership(ev, membershipMap, uid));

        if (joinedEventIds.length === 0) return of(decorate(managedEvents));

        const joinedObservables = joinedEventIds.map((id) =>
          watchDocSafe<AppEvent>(doc(this.firestore, 'events', id))
        );

        return combineLatest(joinedObservables).pipe(
          map((joinedEvents) => [...decorate(managedEvents), ...decorate(joinedEvents)]),
          catchError(() => of(decorate(managedEvents)))
        );
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Événements PUBLICS découvrables dans le hub (visibility=='public').
   * Le filtrage « hors events déjà gérés/rejoints » et « statut actif » se fait
   * côté appelant (signal) pour rester sur un index à deux champs.
   */
  watchPublicEvents(): Observable<AppEvent[]> {
    const q = query(
      collection(this.firestore, 'events'),
      where('visibility', '==', 'public'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    return watchCollectionSafe<AppEvent>(q);
  }

  watchVisibilityGroups(eventId: string): Observable<VisibilityGroup[]> {
    const q = query(
      collection(this.firestore, `events/${eventId}/visibilityGroups`),
      orderBy('order', 'asc')
    );
    return watchCollection<VisibilityGroup>(q);
  }

  watchMembers(eventId: string): Observable<EventMember[]> {
    return watchCollection<EventMember>(
      collection(this.firestore, `events/${eventId}/members`),
      'userId'
    );
  }

  // ─── One-time reads (for guards) ───────────────────────────────────────────

  /**
   * Lecture one-time de la fiche event (réussit si membre, manager, public ou
   * admin). Lecture RÉSILIENTE : `eventViewGuard` en dépend, et un `getDoc` nu
   * n'a aucune borne de temps — sur un réseau qui accepte la connexion sans
   * jamais répondre, sa promesse ne se résout ni en succès ni en erreur et la
   * navigation reste suspendue sans qu'aucun `catch` ne puisse la libérer.
   */
  async getEventOnce(eventId: string): Promise<AppEvent | null> {
    const snap = await getDocResilient(doc(this.firestore, `events/${eventId}`));
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as AppEvent) : null;
  }

  /**
   * Appartenance de l'utilisateur courant. Lecture résiliente : l'affichage du
   * Détail en dépend, elle ne doit jamais rester sans réponse.
   */
  async isMember(eventId: string): Promise<boolean> {
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) return false;
    const snap = await getDocResilient(doc(this.firestore, `events/${eventId}/members/${uid}`));
    return snap.exists();
  }

  async isManager(eventId: string): Promise<boolean> {
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) return false;
    // Résiliente pour la même raison qu'`isMember` : `managerGuard` en dépend.
    const snap = await getDocResilient(doc(this.firestore, `events/${eventId}/members/${uid}`));
    return snap.exists() && snap.data()?.['role'] === 'manager';
  }

  async getMyMembership(eventId: string): Promise<EventMember | null> {
    const uid = this.firebaseAuth.currentUser?.uid;
    if (!uid) return null;
    const snap = await getDocResilient(doc(this.firestore, `events/${eventId}/members/${uid}`));
    return snap.exists() ? (snap.data() as EventMember) : null;
  }

  // ─── Callable Functions ────────────────────────────────────────────────────

  async createEvent(data: {
    name: string;
    type: string;
    estimatedBudgetUsd: number;
    plannedEndDate: string | null;
    acceptedPaymentModes: string[];
    visibility?: 'public' | 'private';
    cotisationMode?: 'none' | 'suggested' | 'required';
    cotisationAmountUsd?: number;
  }): Promise<{ eventId: string; groups: Array<{ id: string; name: string }> }> {
    // createEvent is deployed in europe-west1 (W1).
    return this.callable.call<
      typeof data,
      { eventId: string; groups: Array<{ id: string; name: string }> }
    >(FN_REGION.W1, 'createEvent', data);
  }

  async joinEvent(data: {
    eventId: string;
    /** Présent pour une invitation de groupe ; absent = lien général / event public. */
    groupId?: string;
    /** Token de groupe ou général ; absent = adhésion à un event public découvert. */
    inviteToken?: string;
  }): Promise<{ success: boolean; groupName?: string; alreadyMember?: boolean }> {
    // joinEvent is deployed in europe-west1 (W1).
    return this.callable.call<
      typeof data,
      { success: boolean; groupName?: string; alreadyMember?: boolean }
    >(FN_REGION.W1, 'joinEvent', data);
  }

  /**
   * Publie une annonce à tous les membres. Réservée au gestionnaire côté serveur.
   * Contrairement à la discussion, elle ignore les permissions de groupe.
   */
  async publishAnnouncement(data: { eventId: string; message: string }): Promise<void> {
    await this.callable.call(FN_REGION.W1, 'publishAnnouncement', data);
  }

  async closeEvent(data: {
    eventId: string;
    closureDecision: string;
    notes?: string;
  }): Promise<void> {
    // closeEvent is deployed in europe-west1 (W1).
    await this.callable.call(FN_REGION.W1, 'closeEvent', data);
  }

  /**
   * Quitter un événement (auto-départ). Réservé au membre lui-même. Un manager
   * ne peut pas quitter un événement actif (il doit le clôturer d'abord). Région W1.
   */
  async leaveEvent(data: {
    eventId: string;
  }): Promise<{ success: boolean; alreadyAbsent?: boolean }> {
    return this.callable.call<typeof data, { success: boolean; alreadyAbsent?: boolean }>(
      FN_REGION.W1,
      'leaveEvent',
      data
    );
  }
}
